import React, { useEffect } from 'react';
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import { fetchPosts, resetRand } from "src/actions/community/postsActions";
import {useAppDispatch, useAppSelector} from "src/store/hooks";
import { queryParams } from "src/helpers/UrlHelpers";
import communityUrls from "src/configs/community/communityUrls";
import { setNewParams } from "src/store/community/postsSlice";

import PageLayout from "../_partials/PageLayout";
import PageHeader from "../Posts/_partials/_PageHeader";
import List from "../Posts/_partials/_List";
import Wrapper from "src/componets/Wrapper";
import Filter, { FilterInterface } from "src/componets/Filter";
import NavBarThree from "src/componets/NavBarThree";
import Can from "src/componets/Can";
import { PlusOutlined, RedoOutlined } from "@ant-design/icons";
import New from "../../community/Posts/New";
import ListViewButtons from "src/componets/ListViewButtons";
import { fetchCategories } from "../../../actions/community/categoriesActions";
import { fetchKinds } from "../../../actions/community/kindsActions";
import MenuCategories from "../Categories/_partials/_MenuCategories";
import NewCategory from "../Categories/New";
import EditCategory from "../Categories/Edit";
import CategoriesSettings from "../Categories/Settings";
import NewKind from "../Kinds/New";
import EditKind from "../Kinds/Edit";
import KindsSettings from "../Kinds/Settings";
import {
  selectKindEntities,
  selectKindIds
} from "../../../store/community/kindsSlice";
import {selectAppById} from "../../../store/community/appsSlice";
import {selectSettingById} from "../../../store/community/settingsSlice";

const LIMIT = 42;
const DEFAULT_VIEW = 'grid';
const TARGET = 'content';

const Content = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryId = queryParams.getInt(searchParams,'category_id');
  const kindId = queryParams.getInt(searchParams,'kind_id');
  const currentPage = queryParams.getInt(searchParams,'page') || 1;
  const listType: any = queryParams.get(searchParams,'view') || DEFAULT_VIEW;
  const filterProps: FilterInterface = {};
  const { app_id } = useParams();
  const app = useAppSelector(state => app_id ? selectAppById(state, app_id) : undefined);
  const setting = useAppSelector(state => app?.setting ? selectSettingById(state, app?.setting) : undefined);
  const kinds = useAppSelector(selectKindEntities);
  const kindIds = useAppSelector(selectKindIds);

  const navItems: {[index: number]: any} = {};

  kindIds.forEach((id: any) => {
    const kind = kinds[id];

    if (kind) {
      navItems[kind.id] = {
        id: kind.id,
        name: kind.name,
        url: communityUrls.contentPath({ kind_id: kind.id }),
        isActive: () => kindId === kind.id
      };
    }
  });

  useEffect(() => {
    const params: any = queryParams.requestParams(
      searchParams,
      {
        app_id: app_id,
        limit: LIMIT,
        offset: (currentPage - 1) * LIMIT
      },
      ['term', 'order_by', 'order', 'date_from', 'date_to']
    );

    if (categoryId) {
      params.category_id = categoryId;
    }

    if (kindId) {
      params.kind_id = kindId;
    }

    const categoriesParams: any = { app_id: app_id, target: TARGET };

    if (kindId) {
      categoriesParams.kind_id = kindId;
    }

    dispatch(fetchPosts({ params, target: TARGET }));
    dispatch(fetchCategories({ params: categoriesParams }));
    dispatch(fetchKinds({ params: { app_id: app_id, target: TARGET } }));
  }, [categoryId, kindId, app_id, searchParams, currentPage, dispatch]);

  function onClickCreate() {
    const params: any = { app_id: app_id, target: TARGET };

    params.coins = setting?.data?.content_create?.coins || 5;
    params.purchase_type = setting?.data?.content_create?.purchase_type || 'coins';

    if (categoryId) {
      params.category_id = categoryId;
    }

    if (kindId) {
      params.kind_id = kindId;
    }

    dispatch(setNewParams(params));
  }

  async function onClickResetRand() {
    const id = app_id ? parseInt(app_id) : undefined;

    if (!id) {
      return;
    }

    await dispatch(resetRand({ params: { app_id: id, target: TARGET } }));

    navigate(
      communityUrls.contentPath({ order_by: 'rand', order: 'desc', t: Date.now() }),
      { replace: true }
    );
  }

  filterProps.orderBy = {
    defaultKey: 'id',
    defaultSort: 'desc',
    items: [
      { name: 'Date',       key: 'id' },
      { name: 'Rand',       key: 'rand' },
      { name: 'Popular',    key: 'popular_count' },
      { name: 'Trending',   key: 'trending' },
      { name: 'Likes',      key: 'likes_count' },
      { name: 'Comments',   key: 'comments_count' },
      { name: 'Downloads',  key: 'downloads_count' },
    ]
  };

  return (
    <PageLayout>
      <PageHeader
        title={'Content'}
        navs={navItems}
        target={TARGET}
        parent={{
          name: 'All',
          url: communityUrls.contentPath(),
          isActive: () => !kindId
        }}
      />

      <Can capability='write_community'>
        <Wrapper>
          <NavBarThree>
            <NavBarThree.Button className='v-link2' onClick={onClickResetRand}>
              <RedoOutlined /> Reset Rand
            </NavBarThree.Button>

            <NavBarThree.Button className='v-link2' onClick={onClickCreate}>
              <PlusOutlined /> Create Post
            </NavBarThree.Button>
          </NavBarThree>
        </Wrapper>
      </Can>

      <div className='v-container'>
        <Wrapper type='type-1' border className='v-mb-10'>
          <Filter
            search
            dateRange
            {...filterProps}
          >
            <ListViewButtons className='v-mb-5' align='right' defaultType={DEFAULT_VIEW} />
            <MenuCategories />
          </Filter>
        </Wrapper>

        <List limit={LIMIT} pagination={true} type={listType} />

        <New />

        <NewCategory />
        <EditCategory />
        <CategoriesSettings />

        <NewKind />
        <EditKind />
        <KindsSettings />
      </div>
    </PageLayout>
  );
}

export default Content;
